import { useMemo, useState } from 'react';
import { Box, Button, Checkbox, Flex, Icon, Text, VStack } from '@chakra-ui/react';
import { LuChevronDown, LuChevronUp, LuCircleHelp, LuCheck, LuShieldCheck } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { formatDate } from '../../../utils/intlFormatters';
import { moneyText, renderLine, summarySentences } from '../repairsUI';
import {
  COLLAPSE_AFTER, PREVIEW_ROWS, READ_DIFFERENTLY, groupChanges, isOpen, isQuestion, itemKind,
  movedByCurrency, namesNothing, openQuestions, questionShapes, readTwice, readyCount, rowCounts, sharedAnswer,
  statementSums, verdictOf,
} from './checkupModel';
import ReadTwiceReview from './ReadTwiceReview';
import { ReadTwicePhoneReview } from './ReadTwicePhone';
import { CheckupSheetFooter, FooterNote } from './PhoneCheckupSheet';
import {
  AMOUNT_COLOR, BeforeAfter, StatementSums, Tag, directedMoney, movementLabel,
} from './CheckupParts';
import { chartColor } from '../../shared/TimeSeriesChart';
import { cardSurface } from '../../shared/Card';

// The facts the check carries of a movement, by id.
const factsOf = (item, id) => (item.movements || []).find((movement) => movement.id === id) || null;

// The first sentence of an item's summary that says what it does.
function sentenceOf(item, t, ctx) {
  const sentences = summarySentences(item.keep_lose_gain, t, ctx);
  return sentences.gain[0] || sentences.lose[0] || sentences.keep[0] || sentences.asks[0] || '';
}

// What every question of a bulk says alike: the first sentence of the item
// from a line that names no movement, row or id.
function sharedSentenceOf(item, t, ctx) {
  const lines = item.keep_lose_gain?.lines || [];
  const line = ['gain', 'lose', 'keep', 'asks']
    .flatMap((section) => lines.filter((candidate) => candidate.section === section))
    .find(namesNothing);
  return (line && renderLine(t, line, ctx)) || t('checkup.bulk.alike');
}

// What a row of a group shows on its right: before → after, as its kind reads.
function Figures({ item, kind, currency }) {
  const { t } = useTranslation('repairs');
  const change = item.change || {};
  if (kind === 'sign' || kind === 'amount') {
    return (
      <BeforeAfter
        before={directedMoney(change.before, currency)}
        after={directedMoney(change.after, currency)}
        afterColor={AMOUNT_COLOR[change.after?.direction] || 'redesign.heading'}
      />
    );
  }
  if (kind === 'date') {
    return <BeforeAfter before={formatDate(change.before?.date)} after={formatDate(change.after?.date)} />;
  }
  if (kind === 'description') {
    return <BeforeAfter after={directedMoney(change.after || change.before, currency)} />;
  }
  if (kind === 'gone') {
    return (
      <Flex align="center" gap={2} justify="flex-end">
        <Text fontSize="sm" fontWeight={600} color="redesign.text.muted" textDecoration="line-through">
          {directedMoney(change.before, currency)}
        </Text>
        <Tag tone="red">{t('checkup.changes.removed')}</Tag>
      </Flex>
    );
  }
  if (kind === 'leftover') {
    // The movement stays with today's reading; only the extra one goes.
    return (
      <Flex align="center" gap={2} justify="flex-end" wrap="wrap" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        <Text fontSize="xs" color="redesign.text.muted" textDecoration="line-through">{directedMoney(change.before, currency)}</Text>
        <Tag tone="muted">{t('checkup.readTwice.goes')}</Tag>
        <Text fontSize="sm" fontWeight={600} color={AMOUNT_COLOR[change.after?.direction] || 'redesign.heading'}>
          {directedMoney(change.after, currency)}
        </Text>
        <Tag tone="green">{t('checkup.readTwice.stays')}</Tag>
      </Flex>
    );
  }
  return null;
}

// A question in a row: the Owner's two answers, and "Decide later".
function Question({ item, kind, choice, onChoose, details, names, currency, sums }) {
  const { t } = useTranslation('repairs');
  const change = item.change || {};
  const facts = factsOf(item, change.movement_id) || item.movements?.[0];
  let text;
  let options;
  let cost = null;
  if (READ_DIFFERENTLY.includes(kind)) {
    const yours = kind === 'date'
      ? formatDate(facts?.date)
      : directedMoney({ amount: facts?.amount, direction: change.before?.direction }, currency);
    const statement = kind === 'date' ? formatDate(change.after?.date) : directedMoney(change.after, currency);
    text = t('checkup.question.readDifferently', { statement, yours });
    options = [['useStatement', true], ['keepMine', false]];
    const addsUpAfter = sums.some((entry) => entry.after?.addsUp);
    if (kind === 'amount' && addsUpAfter && facts?.amount != null && change.after?.amount != null) {
      const gap = Math.abs(change.after.amount - Math.abs(facts.amount));
      if (gap >= 0.005) cost = t('checkup.question.keepingCost', { amount: moneyText(gap, currency) });
    }
  } else if (kind === 'gone') {
    text = t('checkup.question.gone');
    options = [['remove', true], ['keepIt', false]];
  } else {
    text = sentenceOf(item, t, { accountName: (id) => names.accounts[id] });
    options = [['yes', true], ['no', false]];
  }
  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" color="redesign.text">{text}</Text>
      <Flex role="radiogroup" aria-label={text} gap={2} wrap="wrap">
        {options.map(([key, value]) => (
          <Button
            key={key}
            role="radio"
            aria-checked={choice === value}
            size="sm"
            variant={choice === value ? 'solid' : 'outline'}
            colorScheme={choice === value ? 'blue' : 'gray'}
            bg={choice === value ? undefined : 'redesign.bg.surface'}
            leftIcon={choice === value ? <LuCheck /> : undefined}
            onClick={() => onChoose(item.id, value)}
          >
            {t(`checkup.question.${key}`)}
          </Button>
        ))}
        {choice != null && (
          <Button size="sm" variant="ghost" onClick={() => onChoose(item.id, null)}>
            {t('checkup.question.later')}
          </Button>
        )}
      </Flex>
      {cost && <Text fontSize="xs" color="redesign.text.muted">{cost}</Text>}
      {choice == null && <Text fontSize="xs" color="redesign.text.muted">{t('checkup.question.waits')}</Text>}
    </VStack>
  );
}

// A row's title and meta: the movement it is about, by name.
function MovementTitle({ id, item, details, names, fallback }) {
  const { t } = useTranslation('repairs');
  if (id == null) {
    return <Text fontSize="sm" fontWeight={600} color="redesign.heading">{fallback || t('checkup.changes.movement')}</Text>;
  }
  const { title, meta } = movementLabel(details[id], factsOf(item, id), names, t);
  return (
    <Box minW={0}>
      <Text fontSize="sm" fontWeight={600} color="redesign.heading" noOfLines={1}>{title}</Text>
      <Text fontSize="xs" color="redesign.text.muted" noOfLines={1}>{meta}</Text>
    </Box>
  );
}

// One change: a checkbox (or, for a question, its answers) and what changes.
function ChangeRow({ item, kind, choice, onChoose, details, names, ctx, sums, asQuestion }) {
  const { t } = useTranslation('repairs');
  const change = item.change || {};
  const currency = change.currency || factsOf(item, change.movement_id)?.currency || item.expected_delta?.[0]?.currency;
  const question = asQuestion && isQuestion(item) && isOpen(item);
  const blocked = item.status === 'blocked';
  const edited = question && READ_DIFFERENTLY.includes(kind);

  const body = ['balance', 'housekeeping', 'other'].includes(kind) || (kind === 'new' && !change.movement_id)
    ? <Text fontSize="sm" color="redesign.text" flex={1}>{sentenceOf(item, t, ctx)}</Text>
    : (
      <Flex flex={1} minW={0} gap={3} align="center" justify="space-between" wrap="wrap">
        <Flex align="center" gap={2} minW={0}>
          <MovementTitle id={change.movement_id} item={item} details={details} names={names} />
          {edited && <Tag tone="orange">{t('checkup.changes.edited')}</Tag>}
        </Flex>
        <Figures item={item} kind={kind} currency={currency} />
      </Flex>
    );

  return (
    <Flex
      gap={3}
      px={[3, 4, 6]}
      py={3}
      align={question ? 'flex-start' : 'center'}
      borderBottom="1px solid"
      borderColor="redesign.border"
      bg={question ? 'phone.alertBg' : undefined}
      boxShadow={question ? `inset 3px 0 0 ${chartColor('phone.alert')}` : undefined}
    >
      <Box w="20px" flexShrink={0} pt={question ? '2px' : 0}>
        {question ? (
          <Icon as={LuCircleHelp} boxSize="18px" color="phone.alert" />
        ) : isOpen(item) ? (
          <Checkbox
            isChecked={choice === true}
            onChange={(e) => onChoose(item.id, e.target.checked)}
            aria-label={t('checkup.changes.kinds.' + kind)}
          />
        ) : null}
      </Box>
      <VStack align="stretch" spacing={3} flex={1} minW={0}>
        {body}
        {blocked && (
          <Text fontSize="xs" color="phone.alertText">
            {t(`item.blocked.${item.blocked_reason}`, { defaultValue: t('item.blocked.generic') })}
          </Text>
        )}
        {question && (
          <Question
            item={item}
            kind={kind}
            choice={choice}
            onChoose={onChoose}
            details={details}
            names={names}
            currency={currency}
            sums={sums}
          />
        )}
      </VStack>
    </Flex>
  );
}

// One of a bulk question's questions, shown as an example: the movement and
// what changes on it.
function BulkExample({ item, kind, details, names, ctx }) {
  const { t } = useTranslation('repairs');
  const change = item.change || {};
  if (['balance', 'housekeeping', 'other', 'new'].includes(kind) && change.movement_id == null) {
    return <Text fontSize="sm" color="redesign.text">{sentenceOf(item, t, ctx)}</Text>;
  }
  const currency = change.currency || factsOf(item, change.movement_id)?.currency;
  return (
    <Flex gap={3} align="center" justify="space-between" wrap="wrap">
      <MovementTitle id={change.movement_id} item={item} details={details} names={names} />
      <Figures item={item} kind={kind} currency={currency} />
    </Flex>
  );
}

/**
 * Many questions that ask the same thing (``questionShapes``): one answer for
 * all of them, the first few as examples, and "one by one" for whoever wants
 * to look at each.
 */
function BulkQuestion({ kind, items, choices, onChooseMany, onOneByOne, details, names, ctx }) {
  const { t } = useTranslation('repairs');
  const [all, setAll] = useState(false);
  const ids = items.map((item) => item.id);
  const answer = sharedAnswer(items, choices);
  let text;
  let options;
  if (READ_DIFFERENTLY.includes(kind)) {
    text = t('checkup.bulk.readDifferently');
    options = [['question.useStatement', true], ['question.keepMine', false]];
  } else if (kind === 'gone') {
    text = t('checkup.bulk.gone');
    options = [['bulk.remove', true], ['bulk.keep', false]];
  } else {
    text = sharedSentenceOf(items[0], t, ctx);
    options = [['question.yes', true], ['question.no', false]];
  }
  const examples = all ? items : items.slice(0, PREVIEW_ROWS);
  return (
    <Box
      px={[3, 4, 6]}
      py={4}
      borderBottom="1px solid"
      borderColor="redesign.border"
      bg="phone.alertBg"
      boxShadow={`inset 3px 0 0 ${chartColor('phone.alert')}`}
    >
      <Flex gap={3} align="flex-start">
        <Icon as={LuCircleHelp} boxSize="18px" color="phone.alert" mt="2px" flexShrink={0} />
        <VStack align="stretch" spacing={3} flex={1} minW={0}>
          <Box>
            <Text fontSize="sm" fontWeight={600} color="redesign.heading">
              {t('checkup.bulk.title', { count: items.length })}
            </Text>
            <Text fontSize="sm" color="redesign.text">{text}</Text>
          </Box>
          <VStack align="stretch" spacing={2}>
            {examples.map((item) => (
              <BulkExample key={item.id} item={item} kind={kind} details={details} names={names} ctx={ctx} />
            ))}
          </VStack>
          {items.length > PREVIEW_ROWS && (
            <Button
              size="xs"
              variant="link"
              alignSelf="flex-start"
              color="redesign.accent.blue"
              onClick={() => setAll((value) => !value)}
            >
              {all ? t('checkup.changes.hide') : t('checkup.bulk.showAll', { count: items.length })}
            </Button>
          )}
          <Flex role="radiogroup" aria-label={text} gap={2} wrap="wrap">
            {options.map(([key, value]) => (
              <Button
                key={key}
                role="radio"
                aria-checked={answer === value}
                size="sm"
                variant={answer === value ? 'solid' : 'outline'}
                colorScheme={answer === value ? 'blue' : 'gray'}
                bg={answer === value ? undefined : 'redesign.bg.surface'}
                leftIcon={answer === value ? <LuCheck /> : undefined}
                onClick={() => onChooseMany(ids, value)}
              >
                {t(`checkup.${key}`)}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={onOneByOne}>{t('checkup.bulk.oneByOne')}</Button>
          </Flex>
          {answer === 'mixed' && <Text fontSize="xs" color="redesign.text.muted">{t('checkup.bulk.mixed')}</Text>}
          {answer == null && <Text fontSize="xs" color="redesign.text.muted">{t('checkup.bulk.waits')}</Text>}
        </VStack>
      </Flex>
    </Box>
  );
}

// The rows a re-read adds: dates and amounts only, as the diff gives them.
function NewRows({ sums }) {
  const { t } = useTranslation('repairs');
  const rows = sums.flatMap((entry) => entry.newRows.map((row) => ({ ...row, currency: entry.currency })));
  return rows.map((row, index) => (
    <Flex
      key={index}
      gap={3}
      px={[3, 4, 6]}
      pl={[10, 12, 14]}
      py={2.5}
      align="center"
      justify="space-between"
      borderBottom="1px solid"
      borderColor="redesign.border"
    >
      <Box minW={0}>
        <Text fontSize="sm" fontWeight={600} color="redesign.heading">{t('checkup.changes.newMovement')}</Text>
        <Text fontSize="xs" color="redesign.text.muted">{formatDate(row.date)}</Text>
      </Box>
      <Flex align="center" gap={2}>
        <Text fontSize="sm" fontWeight={600} color={AMOUNT_COLOR[row.direction]} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {directedMoney(row, row.currency)}
        </Text>
        <Tag tone="blue">{t('checkup.changes.new')}</Tag>
      </Flex>
    </Flex>
  ));
}

// One kind of change: a header with one switch for the whole group, how
// much money it moves, and its rows (the first few when there are many).
function ChangeGroup({
  kind, items, choices, onChoose, onChooseMany, details, names, ctx, sums, questionsInline,
}) {
  const { t } = useTranslation('repairs');
  // On the phone an open question is asked above, one at a time; once
  // answered it comes back here, where the answer can still be changed.
  const waitingHere = (item) => isQuestion(item) && isOpen(item) && choices[item.id] == null;
  // Questions that ask the same thing are answered together, unless the
  // Owner asked to see them one by one. On the phone a bulk question still
  // waiting is asked above, like any other.
  const [oneByOne, setOneByOne] = useState(() => new Set());
  const bulks = questionShapes(items).filter((bulk) => !oneByOne.has(bulk.shape));
  const inBulk = new Set(bulks.flatMap((bulk) => bulk.items.map((item) => item.id)));
  const shownBulks = questionsInline ? bulks : bulks.filter((bulk) => !bulk.items.some(waitingHere));
  const rows = (questionsInline ? items : items.filter((item) => !waitingHere(item)))
    .filter((item) => !inBulk.has(item.id));
  const many = kind === 'housekeeping' || rows.length > COLLAPSE_AFTER;
  const [open, setOpen] = useState(!many || kind !== 'housekeeping');
  const [all, setAll] = useState(false);
  const switchable = items.filter((item) => isOpen(item) && !isQuestion(item));
  const chosen = switchable.filter((item) => choices[item.id] === true).length;
  const moved = movedByCurrency(items);
  const hint = i18n.exists(`repairs:checkup.changes.hints.${kind}`) ? t(`checkup.changes.hints.${kind}`) : '';
  const questions = items.filter(waitingHere).length;
  // Proposals the engine does not call safe start unselected: say so.
  const unchecked = switchable.some((item) => !item.safe && choices[item.id] !== true);
  const shown = !open ? [] : (many && !all && kind !== 'housekeeping' ? rows.slice(0, PREVIEW_ROWS) : rows);
  const hidden = rows.length - shown.length;
  const newRows = kind === 'new' && sums.some((entry) => entry.newRows.length);

  return (
    <Box>
      <Flex
        gap={3}
        px={[3, 4, 6]}
        py={3}
        align="center"
        bg="redesign.bg.hover"
        borderBottom="1px solid"
        borderColor="redesign.border"
      >
        <Box w="20px" flexShrink={0}>
          {switchable.length > 0 && (
            <Checkbox
              isChecked={chosen === switchable.length}
              isIndeterminate={chosen > 0 && chosen < switchable.length}
              onChange={(e) => switchable.forEach((item) => onChoose(item.id, e.target.checked))}
              aria-label={t(`checkup.changes.kinds.${kind}`)}
            />
          )}
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight={600} color="redesign.heading">
            {t(`checkup.changes.kinds.${kind}`)}
            {' · '}
            {kind === 'new' && newRows ? sums.reduce((n, entry) => n + entry.newRows.length, 0) : items.length}
          </Text>
          <Text fontSize="xs" color="redesign.text.muted">
            {[
              moved.length > 0 && t('checkup.changes.moves', {
                amount: moved.map(([currency, amount]) => moneyText(amount, currency)).join(' · '),
              }),
              hint,
              questions > 0 && t(questions === 1 ? 'checkup.changes.questionOne' : 'checkup.changes.questionOther', { count: questions }),
              unchecked && t('checkup.changes.notSelected'),
            ].filter(Boolean).join(' ')}
          </Text>
        </Box>
        {(many || newRows) && rows.length > 0 && (
          <Button
            size="xs"
            variant="ghost"
            color="redesign.accent.blue"
            rightIcon={open ? <LuChevronUp /> : <LuChevronDown />}
            onClick={() => { setOpen((value) => !value); setAll(false); }}
          >
            {open ? t('checkup.changes.hide') : t('checkup.changes.show')}
          </Button>
        )}
      </Flex>
      {open && shownBulks.map((bulk) => (
        <BulkQuestion
          key={bulk.shape}
          kind={kind}
          items={bulk.items}
          choices={choices}
          onChooseMany={onChooseMany}
          onOneByOne={() => setOneByOne((prev) => new Set(prev).add(bulk.shape))}
          details={details}
          names={names}
          ctx={ctx}
        />
      ))}
      {open && newRows && <NewRows sums={sums} />}
      {(!newRows || items.some((item) => isQuestion(item))) && shown.map((item) => (
        <ChangeRow
          key={item.id}
          item={item}
          kind={kind}
          choice={choices[item.id]}
          onChoose={onChoose}
          details={details}
          names={names}
          ctx={ctx}
          sums={sums}
          asQuestion
        />
      ))}
      {open && hidden > 0 && !newRows && (
        <Flex px={[3, 4, 6]} pl={[10, 12, 14]} py={2.5} borderBottom="1px solid" borderColor="redesign.border">
          <Button size="xs" variant="link" color="redesign.accent.blue" onClick={() => setAll(true)}>
            {t('checkup.changes.showOther', { count: hidden })}
          </Button>
        </Flex>
      )}
    </Box>
  );
}

// On the phone, the questions come first, one at a time.
function PhoneQuestions({ items, choices, onChoose, onChooseMany, details, names, ctx, sums }) {
  const { t } = useTranslation('repairs');
  const [oneByOne, setOneByOne] = useState(() => new Set());
  const waiting = openQuestions(items, choices);
  if (!waiting.length) return null;
  const [item] = waiting;
  const kind = itemKind(item);
  const bulk = questionShapes(items).find(
    (candidate) => !oneByOne.has(candidate.shape) && candidate.items.some((other) => other.id === item.id),
  );
  const asked = bulk ? bulk.items.filter((other) => waiting.includes(other)).length : 1;
  const more = waiting.length - asked;
  return (
    <Box bg="redesign.bg.surface" border="1px solid" borderColor="redesign.border" borderRadius="14px" overflow="hidden">
      <Text px={4} pt={3} fontSize="2xs" fontWeight={700} letterSpacing="0.08em" textTransform="uppercase" color="phone.alert">
        {t('checkup.question.label')}
      </Text>
      {bulk ? (
        <BulkQuestion
          kind={kind}
          items={bulk.items}
          choices={choices}
          onChooseMany={onChooseMany}
          onOneByOne={() => setOneByOne((prev) => new Set(prev).add(bulk.shape))}
          details={details}
          names={names}
          ctx={ctx}
        />
      ) : (
        <ChangeRow
          item={item}
          kind={kind}
          choice={choices[item.id]}
          onChoose={onChoose}
          details={details}
          names={names}
          ctx={ctx}
          sums={sums}
          asQuestion
        />
      )}
      {more > 0 && (
        <Text px={4} py={2} fontSize="xs" color="redesign.text.muted">
          {t(more === 1 ? 'checkup.question.moreOne' : 'checkup.question.moreOther', { count: more })}
        </Text>
      )}
    </Box>
  );
}

/**
 * One statement's review: what is off (the statement's own arithmetic),
 * what changes, grouped by kind, and the questions only the Owner can
 * answer. Nothing is sent until Apply: `choices` maps each item to `true`
 * (change it), `false` (leave it) or `null` (a question not answered yet).
 * On the phone the apply button sits in the checkup sheet's footer, and
 * `metaLine` (the file and when it was uploaded) goes under a statement read
 * twice's headline.
 */
export default function CheckupReview({
  run, flags, choices, onChoose, onChooseMany, onApply, onApplyAll, onNotNow, onReview, applying, reviewing, details,
  names, ctx, isPhone, metaLine,
}) {
  const { t } = useTranslation('repairs');
  const sums = useMemo(() => statementSums(run.statement_diff), [run]);
  const groups = useMemo(() => groupChanges(run.items), [run]);
  const verdict = useMemo(() => verdictOf(run), [run]);
  const twice = useMemo(() => readTwice(run), [run]);
  const counts = rowCounts(run.statement_diff);
  // Only extra readings an earlier import left: one decision for all of them.
  if (twice && isPhone) {
    return (
      <ReadTwicePhoneReview
        readTwice={twice}
        sameCount={counts.same}
        details={details}
        names={names}
        metaLine={metaLine}
        onApplyAll={onApplyAll}
        applying={applying}
      />
    );
  }
  if (twice) {
    return (
      <ReadTwiceReview
        readTwice={twice}
        sameCount={counts.same}
        details={details}
        names={names}
        onApplyAll={onApplyAll}
        onNotNow={onNotNow}
        applying={applying}
      />
    );
  }
  const ready = readyCount(run.items, choices);
  const waiting = openQuestions(run.items, choices).length;
  const anyOpen = (run.items || []).some(isOpen);
  // Nothing left to decide, and still a look to give: the Owner may leave it
  // as it is, so the checkup never ends on a statement with no way out.
  const canReview = Boolean(onReview) && !anyOpen && Boolean(flags?.needs_attention);
  const printed = sums.some((entry) => entry.printed);

  const headline = verdict.amount
    ? t(`checkup.verdict.${verdict.key}`, { amount: moneyText(Math.abs(verdict.amount.amount), verdict.amount.currency) })
    : t(
      verdict.count != null ? `checkup.verdict.${verdict.key}${verdict.count === 1 ? 'One' : 'Other'}` : `checkup.verdict.${verdict.key}`,
      { count: verdict.count },
    );
  let sub = null;
  if (run.statement_diff?.reread && counts.different) {
    sub = t(flags?.reprocess_suggested ? 'checkup.verdict.reprocessSub' : 'checkup.verdict.rereadSub', {
      changed: counts.different, total: counts.all,
    });
  } else if (verdict.key === 'nothing') {
    sub = t(run.statement_diff?.reread ? 'checkup.verdict.nothingSubReread' : 'checkup.verdict.nothingSub');
  } else if (verdict.key === 'leftovers') {
    sub = t('checkup.verdict.leftoversSub');
  }

  return (
    <VStack align="stretch" spacing={5} pb={isPhone ? 4 : anyOpen ? '120px' : 6}>
      <Box {...cardSurface} borderRadius="14px" p={[4, 5, 6]}>
        <VStack align="stretch" spacing={4}>
          <Box>
            <Text as="h2" fontSize={['md', 'lg']} fontWeight={600} color="redesign.heading" sx={{ textWrap: 'balance' }}>
              {headline}
            </Text>
            {sub && <Text mt={1} fontSize="sm" color="redesign.text" maxW="700px">{sub}</Text>}
            {!flags?.rereadable && (flags?.reprocess_suggested || flags?.adds_up === false) && (
              <Text mt={1} fontSize="xs" color="redesign.text.muted">{t('checkup.verdict.notRereadable')}</Text>
            )}
          </Box>
          {printed ? (
            <StatementSums sums={sums} ctx={ctx} isPhone={isPhone} />
          ) : run.statement_diff && (
            <Text fontSize="xs" color="redesign.text.muted">{t('checkup.verdict.noBalances')}</Text>
          )}
        </VStack>
      </Box>

      {canReview && (
        <Box {...cardSurface} borderRadius="14px" p={[4, 5, 6]}>
          <Text as="h2" fontSize="md" fontWeight={600} color="redesign.heading">{t('checkup.review.title')}</Text>
          <Text mt={1} fontSize="sm" color="redesign.text" maxW="700px">{t('checkup.review.body')}</Text>
          <Button mt={4} size="sm" colorScheme="blue" leftIcon={<LuCheck />} onClick={onReview} isLoading={reviewing}>
            {t('checkup.review.action')}
          </Button>
        </Box>
      )}

      {isPhone && (
        <PhoneQuestions
          items={run.items}
          choices={choices}
          onChoose={onChoose}
          onChooseMany={onChooseMany}
          details={details}
          names={names}
          ctx={ctx}
          sums={sums}
        />
      )}

      {groups.length > 0 && (
        <Box {...cardSurface} borderRadius="14px" overflow="hidden">
          <Flex px={[3, 4, 6]} py={4} justify="space-between" align="baseline" gap={3} wrap="wrap" borderBottom="1px solid" borderColor="redesign.border">
            <Text as="h2" fontSize="md" fontWeight={600} color="redesign.heading">{t('checkup.changes.title')}</Text>
            <Text fontSize="xs" color="redesign.text.muted">
              {[
                t(groups.reduce((n, group) => n + group.items.length, 0) === 1 ? 'checkup.changes.changeOne' : 'checkup.changes.changeOther', {
                  count: groups.reduce((n, group) => n + group.items.length, 0),
                }),
                waiting > 0 && t(waiting === 1 ? 'checkup.changes.questionOne' : 'checkup.changes.questionOther', { count: waiting }),
              ].filter(Boolean).join(' · ')}
            </Text>
          </Flex>
          {groups.map(({ kind, items }) => (
            <ChangeGroup
              key={kind}
              kind={kind}
              items={items}
              choices={choices}
              onChoose={onChoose}
              onChooseMany={onChooseMany}
              details={details}
              names={names}
              ctx={ctx}
              sums={sums}
              questionsInline={!isPhone}
            />
          ))}
          {counts.same > 0 && (
            <Flex px={[3, 4, 6]} py={3} gap={3} align="center" bg="redesign.bg.hover">
              <Icon as={LuCheck} boxSize="16px" color="redesign.amount.income" />
              <Text fontSize="sm" color="redesign.text">{t('checkup.changes.same', { count: counts.same })}</Text>
            </Flex>
          )}
        </Box>
      )}

      {groups.length > 0 && (
        <Flex align="center" gap={2}>
          <Icon as={LuShieldCheck} boxSize="16px" color="redesign.text.muted" />
          <Text fontSize="xs" color="redesign.text.muted">{t('checkup.changes.keepsYours')}</Text>
        </Flex>
      )}

      {anyOpen && isPhone && (
        <CheckupSheetFooter>
          <Button variant="phonePrimary" w="100%" onClick={onApply} isLoading={applying} isDisabled={!ready}>
            {t(ready === 1 ? 'checkup.footer.applyOne' : 'checkup.footer.applyOther', { count: ready })}
          </Button>
          <FooterNote>
            {ready ? t(ready === 1 ? 'checkup.footer.readyOne' : 'checkup.footer.readyOther', { count: ready }) : t('checkup.footer.nothingReady')}
            <Box as="span">
              {` · ${waiting
                ? t(waiting === 1 ? 'checkup.footer.waitsOne' : 'checkup.footer.waitsOther', { count: waiting })
                : t('checkup.footer.undoAfter')}`}
            </Box>
          </FooterNote>
        </CheckupSheetFooter>
      )}

      {anyOpen && !isPhone && (
        <Flex
          position="sticky"
          bottom={0}
          mx={[-4, -6, -8]}
          px={[4, 6, 8]}
          py={3}
          pb={['calc(12px + env(safe-area-inset-bottom, 0px))', 3]}
          bg="redesign.bg.surface"
          borderTop="1px solid"
          borderColor="redesign.border"
          justify="space-between"
          align={['stretch', 'center']}
          direction={['column', 'row']}
          gap={3}
          zIndex={2}
        >
          <Box>
            <Text fontSize="sm" fontWeight={600} color="redesign.heading">
              {ready ? t(ready === 1 ? 'checkup.footer.readyOne' : 'checkup.footer.readyOther', { count: ready }) : t('checkup.footer.nothingReady')}
            </Text>
            <Text fontSize="xs" color="redesign.text.muted">
              {waiting
                ? t(waiting === 1 ? 'checkup.footer.waitsOne' : 'checkup.footer.waitsOther', { count: waiting })
                : t('checkup.footer.undoAfter')}
            </Text>
          </Box>
          <Flex gap={2} direction={['column-reverse', 'row']}>
            <Button variant="outline" onClick={onNotNow} isDisabled={applying}>{t('checkup.footer.notNow')}</Button>
            <Button colorScheme="blue" onClick={onApply} isLoading={applying} isDisabled={!ready}>
              {t(ready === 1 ? 'checkup.footer.applyOne' : 'checkup.footer.applyOther', { count: ready })}
            </Button>
          </Flex>
        </Flex>
      )}
    </VStack>
  );
}
